import React, {useCallback, useEffect, useRef} from "react";
import {IconButton, Tooltip} from "@mui/material";
import { DarkModeIcon, LightModeIcon } from "../../icons";
import {observer} from "mobx-react-lite";
import {useRootStore} from "../../store/contexts";

/**
 * Alterna entre claro y oscuro desde la barra superior.
 *
 * El cambio se ve al instante y la preferencia se guarda en el perfil por
 * detrás, así que sobrevive a la recarga y acompaña al usuario a otro
 * dispositivo. El modo también queda cacheado localmente —eso lo hace
 * `PrefStore.setTheme`— para que al abrir la aplicación no se vea un destello
 * en claro mientras llega el perfil.
 *
 * No muestra confirmación a propósito: la pantalla entera cambió de color, que
 * es todo el acuse de recibo que hace falta.
 */
export const ThemeToggle: React.FC = observer(() => {
  const {prefStore, authStore, langStore} = useRootStore();
  const esOscuro = prefStore.paletteType === "dark";

  // Guardar es lo único que va al servidor, así que se difiere: quien alterna
  // varias veces seguidas para comparar produce una sola escritura, la última.
  const pendiente = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (pendiente.current) clearTimeout(pendiente.current);
    },
    []
  );

  const alternar = useCallback(() => {
    const siguiente = esOscuro ? "light" : "dark";
    prefStore.setTheme(siguiente);

    if (pendiente.current) clearTimeout(pendiente.current);
    pendiente.current = setTimeout(() => {
      pendiente.current = null;
      // Si falla, la vista y la caché local ya quedaron en el modo elegido: el
      // usuario ve lo que pidió y el perfil se pone al día en el próximo
      // cambio. No hay nada útil que decirle acá.
      Promise.resolve(authStore.saveTheme(siguiente)).catch(() => undefined);
    }, 600);
  }, [esOscuro, prefStore, authStore]);

  return (
    <Tooltip title={esOscuro ? langStore.themeLabel : langStore.darkModeLabel}>
      <IconButton onClick={alternar} color="inherit" size="large">
        {esOscuro ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
    </Tooltip>
  );
});
